// ============================================================
// THE REST OF ITS TRAITS (plan body-map T2; the bench's "traits without a
// look, now with one" and the senses, ruled 2026-10-09):
//
//   Natural Armour (X)      scales over its body, denser and plainer with X;
//                           they flash where a hit lands
//   Sturdy                  its feet planted; the floor cracks under a hit
//   Black Carapace          the sights only: they lock a size tighter
//   Deadly Natural Weapons  its fists pulse blood red (the toxic veins' look)
//   Swift                   speed streaks off its back edge, and a ghost of it
//                           a step behind
//   Strange Physiology      the sights only: a lock slides off it
//   Frenzied                straining: a constant small tremor, its outline
//                           tensed red
//   Blind                   listening: sound arrives at its head from both
//                           sides, and the side it reaches lights; Blinded is
//                           refused (it never saw)
//
// Pure. Zero React dependency. Frame units; seconds.
// ============================================================

import type { Box } from './frame';
import { hash01, seeded } from './hash';
import { loop } from './tracks';

/** Natural Armour (X): the scales' size (smaller, denser with X) and how strongly they show. */
export const scalesLook = (x: number) => ({ size: 110 - 10 * x, opacity: Number((0.22 + 0.06 * x).toFixed(2)) });

/** Sturdy: a planted mark either side of its feet, at the ground - out along it, then up. */
export function sturdyFeet([x0, , x1, y1]: Box): { d: string }[] {
  const cx = (x0 + x1) / 2;
  return [-1, 1].map((dx) => ({ d: `M${cx + dx * 60} ${y1 + 30} h${dx * 170} m${-dx * 170} 0 v-50` }));
}

/** Sturdy struck: the floor cracks out from its feet both ways (seeded by the attack: the same on every screen). */
export function sturdyCracks([x0, , x1, y1]: Box, seed: string): string[] {
  const cx = (x0 + x1) / 2;
  const fy = y1 + 30;
  const r = seeded(`sturdy:${seed}`);
  return [-1, 1].map((dx) => {
    const legs = Array.from({ length: 6 }, (_, n) => {
      const jy = (r(`${dx}:${n}`) - 0.5) * (n < 5 ? 60 : 40);
      return `L${Math.round(cx + dx * (80 + (340 * (n + 1)) / 6))} ${Math.round(fy + jy)}`;
    });
    return `M${cx + dx * 80} ${fy} ${legs.join(' ')}`;
  });
}

/** Swift: ten streaks off its back edge the whole height of it, each its own length and beat. */
export function swiftStreaks([x0, y0, , y1]: Box) {
  return Array.from({ length: 10 }, (_, k) => {
    const y = y0 + (y1 - y0) * (0.08 + 0.84 * (k / 9));
    const x1 = x0 - 30;
    return {
      y,
      x1,
      x2: Math.round(x1 - 220 - 260 * hash01(k, 17)),
      opacity: loop('opacity', [0, 0.8, 0], Number((1.1 + (k % 4) * 0.3).toFixed(1)), {
        offset: Number((k * 0.23).toFixed(2)),
      }),
    };
  });
}

/** Blind: three arcs of sound from each side, drawn in from far out to the head's sides, out of step. */
export function listenArcs([hx0, hy0, hx1, hy1]: Box) {
  const cx = (hx0 + hx1) / 2;
  const y = hy0 + 0.42 * (hy1 - hy0);
  return [-1, 1].flatMap((side) =>
    [0, 1, 2].map((k) => ({ side, y, from: cx + side * 700, to: cx + side * (hx1 - hx0) * 0.6, offset: k * 0.7 })),
  );
}

/** The conditions a body's plate draws: a Blind body refuses Blinded (code `B`) - it never saw. */
export const withoutRefused = (conditions: readonly string[], blind: boolean) =>
  blind ? conditions.filter((c) => c !== 'B') : [...conditions];
